import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ExternalLink, Pencil, Plus, Star, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { catalogAPI, coursesAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { t } from '../../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';
import {
  Badge, DataTable, EmptyPanel, FilterPills, IconButton, PageHeader, RowMenu, SearchBox, SelectBox,
  StatCards, TableRow, Thumb, adminBtnOutline, adminBtnPrimary, fmtDate, statusBadge,
} from '../../components/adminUi';

// Admin 1 · Courses (s134 redesign): header + New course, stat cards, status
// pills with counts, search + category filter, rich table (thumb, title, lessons
// · students, Top tag, category › sub, price, status, updated, edit + ⋮).
const COLS = [
  { label: 'Course', width: 'minmax(0,3.2fr)' },
  { label: 'Category', width: 'minmax(0,1.6fr)' },
  { label: 'Price', width: '110px' },
  { label: 'Status', width: '130px' },
  { label: 'Students', width: '110px', adminOnly: true },
  { label: 'Updated', width: '120px' },
  { label: 'Actions', width: '90px', align: 'right' },
];

function PriceCell({ c }) {
  if (c.is_free) return <span className="text-sm font-bold text-lms-free">{t('Free')}</span>;
  return (
    <span className="text-sm font-bold text-lms-ink lms-num">
      ${Number(c.discount_flag ? c.discounted_price : c.price || 0).toFixed(2)}
      {c.discount_flag ? <span className="ml-1.5 text-xs font-normal text-lms-faint line-through">${Number(c.price).toFixed(0)}</span> : null}
    </span>
  );
}

export default function CoursesPage() {
  const navigate = useNavigate();
  const staff = ['admin', 'manager'].includes((getUser() || {}).lr);
  // s155: Students column (shortcut to the course's Students tab) — admin only
  const isAdmin = (getUser() || {}).lr === 'admin';
  const cols = COLS.filter((c) => !c.adminOnly || isAdmin);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [cats, setCats] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const perPage = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await coursesAPI.list({ search, status, category_id: category, page, per_page: perPage });
      setRows(data.courses);
      setTotal(data.total);
      setSummary(data.summary || null);
    } catch {
      toast.error(t('Could not load courses'));
    } finally {
      setLoading(false);
    }
  }, [search, status, category, page]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0); // debounce typing
    return () => clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    if (!staff) return;
    catalogAPI.list().then(({ data }) => setCats(data.categories || data || [])).catch(() => {});
  }, [staff]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await coursesAPI.create({ title: newTitle });
      navigate(`/admin/courses/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
      setBusy(false);
    }
  };

  const del = async (course) => {
    if (!window.confirm(`${t('Delete')} "${course.title}"?`)) return;
    try {
      await coursesAPI.remove(course.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const toggleTop = async (course) => {
    try {
      await coursesAPI.update(course.id, { is_top: course.is_top ? 0 : 1 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const bs = (summary && summary.by_status) || {};
  const pages = Math.max(1, Math.ceil(total / perPage));
  const catList = Array.isArray(cats) ? cats : [];

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader
        title={staff ? t('Courses') : t('My courses')}
        subtitle={t('Create, edit and publish the courses of your academy.')}
      >
        <button type="button" onClick={() => { setCreating(true); setNewTitle(''); }} className={adminBtnPrimary}>
          <Plus size={16} strokeWidth={2.4} /> {t('New course')}
        </button>
      </PageHeader>

      {summary && (
        <StatCards
          items={[
            { label: t('Courses'), value: summary.total },
            { label: t('Published'), value: bs.active || 0 },
            { label: t('Lessons'), value: summary.lessons },
            { label: t('Enrollments'), value: summary.enrollments },
          ]}
        />
      )}

      <FilterPills
        value={status}
        onChange={(v) => { setStatus(v); setPage(1); }}
        items={[
          { key: '', label: t('All'), count: summary ? summary.total : undefined },
          { key: 'active', label: t('Active'), count: bs.active },
          { key: 'pending', label: t('Pending review'), count: bs.pending },
          { key: 'draft', label: t('Draft'), count: bs.draft },
        ]}
      >
        <SearchBox value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('Search courses…')} />
        {staff && catList.length > 0 && (
          <SelectBox value={category} onChange={(v) => { setCategory(v); setPage(1); }} label={t('Category')}>
            <option value="">{t('All categories')}</option>
            {catList.map((c) => <option key={c.id} value={c.id}>{c.parent_id ? `— ${c.name}` : c.name}</option>)}
          </SelectBox>
        )}
      </FilterPills>

      <DataTable
        cols={cols}
        empty={!loading && rows.length === 0 ? (
          <EmptyPanel bare icon={BookOpen} title={search || status || category ? t('No courses match these filters.') : t('No courses yet')}>
            {!(search || status || category) && t('Create your first course to start building its curriculum.')}
          </EmptyPanel>
        ) : null}
      >
        {rows.map((c) => (
          <TableRow key={c.id} cols={cols} onClick={() => navigate(`/admin/courses/${c.id}`)}>
            <div className="flex min-w-0 items-center gap-3.5">
              <Thumb src={c.thumbnail} title={c.title} w={72} h={40} />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-[14.5px] font-bold text-lms-ink">{c.title}</span>
                  {c.is_top ? <Badge kind="warn">{t('Top')}</Badge> : null}
                </div>
                <div className="text-[12.5px] text-lms-sub lms-num">
                  {c.lesson_count || 0} {c.lesson_count === 1 ? t('lesson') : t('lessons')} · {c.student_count || 0} {c.student_count === 1 ? t('student') : t('students')}
                </div>
              </div>
            </div>
            <span className={`truncate text-[13.5px] ${c.category_name ? 'text-lms-body' : 'text-lms-faint'}`}>
              {[c.category_name, c.sub_category_name].filter(Boolean).join(' › ') || t('No category')}
            </span>
            <PriceCell c={c} />
            {statusBadge(c.status)}
            {isAdmin && (
              <Link
                to={`/admin/courses/${c.id}?tab=students`}
                onClick={(e) => e.stopPropagation()}
                aria-label={`${t('Students')}: ${c.student_count || 0}`}
                title={t('View students')}
                className="lms-press inline-flex h-9 items-center gap-1.5 justify-self-start rounded-lg border border-lms-line bg-lms-surface px-3 text-[13px] font-bold text-lms-ink hover:border-lms-ink"
              >
                <Users size={14} /> <span className="lms-num">{c.student_count || 0}</span>
              </Link>
            )}
            <span className="text-[13px] text-lms-sub">{fmtDate(c.updated_at || c.created_at)}</span>
            <span className="flex justify-end gap-1">
              <IconButton label={t('Edit course')} onClick={() => navigate(`/admin/courses/${c.id}`)}><Pencil size={15} /></IconButton>
              <RowMenu
                items={[
                  c.status === 'active' && { label: t('View in site'), icon: ExternalLink, onClick: () => window.open(`/course/${c.slug}`, '_blank', 'noopener') },
                  staff && { label: c.is_top ? t('Remove from top') : t('Mark as top'), icon: Star, onClick: () => toggleTop(c) },
                  { label: t('Delete'), icon: Trash2, onClick: () => del(c), danger: true },
                ]}
              />
            </span>
          </TableRow>
        ))}
      </DataTable>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={adminBtnOutline}>{t('Previous')}</button>
          <span className="text-sm text-lms-sub lms-num">{page} / {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className={adminBtnOutline}>{t('Next')}</button>
        </div>
      )}

      {creating && (
        <Modal title={t('New course')} onClose={() => setCreating(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label={t('Title')}>
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required autoFocus className={inputCls} />
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy || !newTitle.trim()} className={btnPrimary}>
                {busy ? t('Creating…') : t('Create')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
